<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">問卷統計</h1>
      </div>
      <div class="header-actions">
        <router-link class="btn btn-secondary" :to="{ name: 'QuestionnaireList' }">問卷列表</router-link>
        <router-link class="btn btn-secondary" :to="{ name: 'QuestionnaireResponses', params: { id: $route.params.id } }">回覆</router-link>
        <router-link class="btn btn-primary" :to="{ name: 'EditQuestionnaire', params: { id: $route.params.id } }">編輯問卷</router-link>
      </div>
    </div>
    <div class="page-container">
      <p v-if="!stats" class="stats-empty">載入中</p>
      <template v-else>
        <div class="stats-summary">
          <div class="summary-card">
            <span class="summary-value">{{ stats.responseCount }}</span>
            <span class="summary-label">成功送出</span>
          </div>
          <div class="summary-card">
            <span class="summary-value">{{ stats.identifiedRespondents }}</span>
            <span class="summary-label">可識別人數</span>
          </div>
        </div>

        <div class="view-switch" role="group" aria-label="統計顯示方式">
          <button type="button" :class="{ on: view === 'bar' }" @click="view = 'bar'">長條</button>
          <button type="button" :class="{ on: view === 'pie' }" @click="view = 'pie'">圓餅圖</button>
        </div>

        <p v-if="!stats.questions.length && !stats.removedQuestions.length" class="stats-empty">尚無題目</p>

        <section v-for="(question, index) in stats.questions" :key="question.questionId" class="q-card">
          <header class="q-head">
            <span class="q-index">{{ index + 1 }}</span>
            <div>
              <h2>{{ question.prompt || '未命名題目' }}</h2>
              <p class="q-meta">
                <span class="q-type">{{ typeLabel(question.questionType) }}</span>
                <span v-if="answerCount(question) != null">{{ answerCount(question) }} 份回覆</span>
                <span v-if="question.average != null">平均 {{ question.average.toFixed(2) }}</span>
              </p>
            </div>
          </header>

          <p v-if="question.questionType === 'multi'" class="q-note">占比以回覆人數為分母，各選項合計可超過 100%。</p>

          <div v-if="question.options?.length && view === 'pie'" class="pie-wrap">
            <PieChart
              v-if="pieData(question).length"
              :data="pieData(question)"
              :show-title="false"
              title="回覆"
              height="280px"
              :colors="pieColors"
            />
            <p v-else class="q-note">尚無回覆，有人作答後會顯示圓餅圖。</p>
          </div>

          <div v-if="question.options?.length" class="options">
            <div v-for="option in question.options" :key="option.id" class="option">
              <div class="option-head">
                <span>{{ option.label }}</span>
                <span>{{ option.count }} 人（{{ percent(option.ratio) }}%）</span>
              </div>
              <div v-if="view === 'bar'" class="track" :aria-label="`${option.label} ${percent(option.ratio)}%`">
                <span class="fill" :style="{ width: barWidth(option.ratio) }"></span>
              </div>
            </div>
          </div>

          <div v-if="question.previousOptions?.length" class="sub">
            <h3>先前選項</h3>
            <div v-for="option in question.previousOptions" :key="option.label" class="text-row">
              <span>{{ option.label }}</span>
              <span>{{ option.count }} 人</span>
            </div>
          </div>

          <div v-if="question.otherTexts?.length" class="sub">
            <h3>其他文字</h3>
            <div v-for="(item, itemIndex) in question.otherTexts" :key="itemIndex" class="text-row">
              <span class="pre">{{ item.text }}</span>
              <time>{{ formatTime(item.submittedAt) }}</time>
            </div>
          </div>

          <div v-if="isText(question.questionType)" class="sub">
            <p v-if="!question.texts?.length" class="q-note">尚無文字回覆</p>
            <div v-for="(item, itemIndex) in question.texts || []" :key="itemIndex" class="text-row">
              <span class="pre">{{ item.text }}</span>
              <time>{{ formatTime(item.submittedAt) }}</time>
            </div>
          </div>
        </section>

        <section v-if="stats.removedQuestions.length" class="q-card removed">
          <h2>已刪除題目</h2>
          <div v-for="question in stats.removedQuestions" :key="question.questionId" class="text-row">
            <span>{{ question.prompt || '未命名題目' }}</span>
            <span>{{ question.responseCount }} 份</span>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import { questionnaireService } from '@/api/modules/questionnaires.js';
import PieChart from '@/components/charts/PieChart.vue';

const TYPE_LABELS = {
  single: '選擇題',
  multi: '核取方塊',
  scale: '線性刻度',
  short_text: '簡答',
  long_text: '詳答'
};

export default {
  name: 'QuestionnaireStats',
  components: { PieChart },
  data() {
    return {
      stats: null,
      view: 'bar',
      pieColors: ['#667eea', '#764ba2', '#5746e3', '#8b7cf7', '#34d399', '#60a5fa', '#f59e0b', '#f093fb', '#fa709a', '#94a3b8']
    };
  },
  async mounted() {
    const response = await questionnaireService.stats(this.$route.params.id);
    const data = response?.data || {};
    this.stats = {
      responseCount: data.responseCount || 0,
      identifiedRespondents: data.identifiedRespondents || 0,
      questions: data.questions || [],
      removedQuestions: data.removedQuestions || []
    };
  },
  methods: {
    typeLabel(type) {
      return TYPE_LABELS[type] || type;
    },
    isText(type) {
      return type === 'short_text' || type === 'long_text';
    },
    answerCount(question) {
      if (question.responseCount != null) return question.responseCount;
      if (this.isText(question.questionType)) return question.texts?.length || 0;
      return null;
    },
    percent(ratio) {
      return Math.round((ratio || 0) * 100);
    },
    barWidth(ratio) {
      const value = this.percent(ratio);
      if (value <= 0) return '0%';
      return `${Math.min(100, Math.max(value, 2))}%`;
    },
    pieData(question) {
      return (question.options || [])
        .filter((option) => option.count > 0)
        .map((option) => ({ name: option.label, value: option.count }));
    },
    formatTime(value) {
      return value ? new Date(value).toLocaleString('zh-TW') : '';
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.view-switch {
  display: inline-flex;
  margin-bottom: 16px;
  padding: 3px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.view-switch button {
  border: 0;
  background: transparent;
  color: #5f6368;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 14px;
  cursor: pointer;
}

.view-switch button.on {
  background: #667eea;
  color: #fff;
}

.pie-wrap {
  margin-top: 8px;
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-value {
  font-size: 28px;
  font-weight: 650;
  line-height: 1;
  color: #202124;
}

.summary-label {
  color: #5f6368;
  font-size: 13px;
}

.q-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 12px;
}

.q-card.removed {
  background: #fafafa;
}

.q-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.q-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #eef0ff;
  color: #5746e3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex: none;
}

.q-card h2 {
  margin: 2px 0 0;
  font-size: 16px;
  font-weight: 600;
}

.q-meta {
  margin: 6px 0 0;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  color: #5f6368;
  font-size: 13px;
}

.q-type {
  background: #f4f2ff;
  color: #5746e3;
  border-radius: 99px;
  padding: 2px 8px;
}

.q-note {
  margin: 12px 0 0;
  color: #5f6368;
  font-size: 13px;
}

.options {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  margin-bottom: 6px;
}

.track {
  height: 8px;
  background: #eef0f4;
  border-radius: 99px;
  overflow: hidden;
}

.fill {
  display: block;
  height: 100%;
  background: #667eea;
  border-radius: 99px;
}

.sub {
  margin-top: 16px;
}

.sub h3 {
  margin: 0 0 4px;
  font-size: 13px;
  color: #5f6368;
  font-weight: 600;
}

.text-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
  font-size: 14px;
}

.text-row time,
.text-row > span:last-child {
  color: #5f6368;
  white-space: nowrap;
}

.pre {
  white-space: pre-wrap;
}

.stats-empty {
  color: #5f6368;
}

@media (max-width: 640px) {
  .stats-summary {
    grid-template-columns: 1fr 1fr;
  }

  .text-row {
    flex-direction: column;
  }
}
</style>
